import { type FC } from "react"

import { cn } from "@forento/shared-client/utilities/tailwind"
import { type PersonalizedCourseExam } from "@forento/shared/models/course"

import Button from "~/themes/school/components/Button"
import Certificate from "~/themes/school/components/Certificate"
import Checkbox, { Radio } from "~/themes/school/components/Checkbox"
import { CheckIcon, CloseIcon } from "~/themes/school/components/Icon"
import { useTranslation } from "~/translations"
import routes from "~/utilities/routes"

type ExamQuestion = { label: string; options: { id: number; label: string; isCorrect: boolean }[] }

type Props = { exam: Extract<PersonalizedCourseExam, { isCompleted: true }> }
const Result: FC<Props> = ({ exam: { exam, completion, course } }) => {
	const t = useTranslation()

	return (
		<div className="flex flex-col gap-10">
			<div className="flex max-w-4xl flex-col items-center bg-white p-6 shadow">
				{completion.score.received >= exam.passScore ? (
					<div className="mb-2 flex flex-col items-center gap-6">
						<div className="h-18 w-18 rounded-full bg-success-light p-4">
							<CheckIcon className="h-full w-full text-success" />
						</div>
						<p className="text-md text-center font-quicksand font-bold">{t("course.exam.passed")}</p>
					</div>
				) : (
					<div className="mb-2 flex flex-col items-center gap-6">
						<div className="h-18 w-18 rounded-full bg-danger-light p-4">
							<CloseIcon className="h-full w-full text-danger" />
						</div>
						<p className="text-md text-center font-quicksand font-bold">{t("course.exam.failed")}</p>
					</div>
				)}
				<p className="mb-2 text-center font-quicksand text-3xl font-bold">
					{completion.score.received}/{completion.score.total}
				</p>
				<p className="text-sm">{t("course.exam.passingScore", { passScore: exam.passScore })}</p>
			</div>
			{exam.certificate && (
				<div className="flex max-w-4xl flex-col items-center gap-4 bg-white p-6 shadow">
					<Certificate certificate={{ course, createDate: exam.certificate.createDate }} />
					<Button variant="primary" onClick={routes.certificate.detail(exam.certificate.id)}>
						{t("certificate.goTo")}
					</Button>
				</div>
			)}
			<div className="flex max-w-4xl flex-col items-center gap-4 bg-white p-6 shadow">
				{exam.questions.map(question =>
					question.options.filter(x => x.isCorrect).length === 1 ? (
						<SingleChoice key={question.id} question={question} answers={completion.answers.all} />
					) : (
						<MultipleChoice key={question.id} question={question} answers={completion.answers.all} />
					),
				)}
			</div>
		</div>
	)
}

type QuestionProps = { question: ExamQuestion; answers: number[] }
const SingleChoice: FC<QuestionProps> = ({ question, answers }) => (
	<>
		<h2 className="mb-2 text-center font-quicksand text-xl font-bold">{question.label}</h2>
		<div className="mb-6 flex flex-col gap-4">
			{question.options.map(option => (
				<Radio
					key={option.id}
					label={option.label}
					isChecked={answers.includes(option.id)}
					className={cn(option.isCorrect !== answers.includes(option.id) && "text-danger line-through")}
				/>
			))}
		</div>
	</>
)

const MultipleChoice: FC<QuestionProps> = ({ question, answers }) => (
	<>
		<h2 className="mb-2 text-center font-quicksand text-xl font-bold">{question.label}</h2>
		<div className="mb-6 flex flex-col gap-4">
			{question.options.map(option => (
				<Checkbox
					key={option.id}
					label={option.label}
					isChecked={answers.includes(option.id)}
					className={cn(option.isCorrect !== answers.includes(option.id) && "text-danger line-through")}
				/>
			))}
		</div>
	</>
)

export default Result
